// Lib
import { useMemo } from 'react';

// Utils
import { getHeight, getWidth } from '../../../../../../../common/maths/geometry/rect';
import {
    getLargestImageSize,
    getOriginalImage,
    getOriginalImageHeight,
    getOriginalImageWidth,
} from '../../../imageModalUtils';
import { getImageSource } from '../../../../imageHelper';
import { fitSizeWithinBounds } from '../../../imageModalSizingUtils';
import { getImageModalEditorSource } from '../../../content/utils/imageModalEditorSourceUtils';

// Types
import type { ImMNElement, MNImageContent } from '../../../../../../../common/elements/elementModelTypes';

type UseImageModalV2EditorSourcesOptions = {
    element: ImMNElement;
    elementId: string;
    gridSize: number;
    attachment?: unknown;
    editorBounds: { width: number; height: number };
    backgroundRemovalEditSource: MNImageContent | undefined;
    imageType: string;
    shouldUseOriginalEditorSource: boolean;
    isRotatedOnSide: boolean;
    // Session starting orientation — part of the load key so the cropper reloads when it changes.
    initialRotation: number;
    initialScaleX: number;
    initialScaleY: number;
    useSecureMediaUrl: boolean;
    renderSecureImagesThroughCF: boolean;
};

/**
 * Resolves everything about *which* image the V2 editor is operating on: the crop-source image for the
 * active mode (edited vs original), the full (unedited) source the save path composes against, the URLs
 * each of them loads from, the prefetch descriptor for the crop-tool's alternate source, the fitted
 * editor footprint, and the load key that tells the loader when the source has genuinely changed.
 */
export const useImageModalV2EditorSources = ({
    element,
    elementId,
    gridSize,
    attachment,
    editorBounds,
    backgroundRemovalEditSource,
    imageType,
    shouldUseOriginalEditorSource,
    isRotatedOnSide,
    initialRotation,
    initialScaleX,
    initialScaleY,
    useSecureMediaUrl,
    renderSecureImagesThroughCF,
}: UseImageModalV2EditorSourcesOptions) => {
    // The image the cropper displays for the active mode: the edited render normally, the original
    // source when the crop tool is active (so stored crops can be adjusted rather than re-cropped).
    const cropSourceImage = getImageModalEditorSource({
        element,
        fullSourceVariant: backgroundRemovalEditSource,
        useOriginalSource: shouldUseOriginalEditorSource,
    });

    const originalImageWidth = getWidth(cropSourceImage) || getOriginalImageWidth({ element, gridSize, attachment });
    const originalImageHeight = getHeight(cropSourceImage) || getOriginalImageHeight({ element, gridSize, attachment });

    // Flip the image width and height if it's rotated onto its side
    const imageWidth = isRotatedOnSide ? originalImageHeight : originalImageWidth;
    const imageHeight = isRotatedOnSide ? originalImageWidth : originalImageHeight;

    const { width: modalWidth, height: modalHeight } = fitSizeWithinBounds(
        { width: imageWidth, height: imageHeight },
        editorBounds,
    );

    const imageSize = getLargestImageSize(cropSourceImage);
    const largestUneditedImageUrl = useMemo(
        () =>
            getImageSource({
                imageDetails: cropSourceImage,
                imageSize,
                elementId,
                useSecureMediaUrl,
                renderSecureImagesThroughCF,
            }),
        [cropSourceImage, imageSize, elementId, useSecureMediaUrl, renderSecureImagesThroughCF],
    );

    // The full (unedited) source the save path composes stored + session transforms against.
    const fullSourceImage = backgroundRemovalEditSource || getOriginalImage(element);
    const fullSourceImageWidth = getWidth(fullSourceImage) || getOriginalImageWidth({ element, gridSize, attachment });
    const fullSourceImageHeight =
        getHeight(fullSourceImage) || getOriginalImageHeight({ element, gridSize, attachment });
    const fullSourceImageUrl = useMemo(
        () =>
            getImageSource({
                imageDetails: fullSourceImage,
                imageSize: getLargestImageSize(fullSourceImage),
                elementId,
                useSecureMediaUrl,
                renderSecureImagesThroughCF,
            }),
        [fullSourceImage, elementId, useSecureMediaUrl, renderSecureImagesThroughCF],
    );

    // The source the crop toggle would switch to, decoded ahead of the click so the swap doesn't flash.
    const alternateSourceImage = getImageModalEditorSource({
        element,
        fullSourceVariant: backgroundRemovalEditSource,
        useOriginalSource: !shouldUseOriginalEditorSource,
    });
    const alternateSourceUrl = useMemo(
        () =>
            getImageSource({
                imageDetails: alternateSourceImage,
                imageSize: getLargestImageSize(alternateSourceImage),
                elementId,
                useSecureMediaUrl,
                renderSecureImagesThroughCF,
            }),
        [alternateSourceImage, elementId, useSecureMediaUrl, renderSecureImagesThroughCF],
    );
    const prefetchEditorSource = useMemo(
        () => (alternateSourceUrl ? { sourceUrl: alternateSourceUrl, imageType } : null),
        [alternateSourceUrl, imageType],
    );

    const editorImageLoadKey = `${elementId}-${imageType}-${shouldUseOriginalEditorSource}-${initialRotation}-${initialScaleX}-${initialScaleY}-${largestUneditedImageUrl || ''}`;

    return {
        cropSourceImage,
        fullSourceImage,
        fullSourceImageWidth,
        fullSourceImageHeight,
        modalWidth,
        modalHeight,
        largestUneditedImageUrl,
        fullSourceImageUrl,
        alternateSourceUrl,
        prefetchEditorSource,
        editorImageLoadKey,
    };
};
